export type Insight = {
  slug: string;
  number: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  image: string;
  imageAlt: string;
  pullQuote: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
};

export const insights: Insight[] = [
  {
    slug: 'counterfeit-stakes',
    number: '01',
    title: 'Counterfeit Stakes',
    excerpt: 'The feeling is real. The arena is not. Why engineered risk can consume the same engine that built everything else in your life.',
    category: 'RISK',
    readTime: '6 MIN READ',
    image: '/assets/page09-who-for.jpg',
    imageAlt: 'A solitary man standing in a dark blue landscape',
    pullQuote: 'The engine is yours. The machine only borrowed it.',
    sections: [
      {
        heading: 'The stakes feel real because your body makes them real.',
        paragraphs: [
          'The pulse, the tunnel vision, the absolute certainty that the next decision matters—none of that is imaginary. Your nervous system is doing exactly what it was built to do when something important is on the line.',
          'The counterfeit part is the arena. A sportsbook did not create your appetite for competition, pattern recognition, or pressure. It built a machine that can rent those strengths by the minute and keep the door open all night.'
        ]
      },
      {
        heading: 'Real risk pays you back.',
        paragraphs: [
          'A difficult conversation, a decisive business move, a hard training block, a public commitment—real risk leaves something behind. Skill. Trust. Capacity. Evidence that you can command yourself under pressure.',
          'Counterfeit stakes offer the sensation without the residue. The result is more hunger, less command, and another reason to come back for the feeling you just spent.'
        ]
      }
    ]
  },
  {
    slug: 'the-attention-tax',
    number: '02',
    title: 'The Attention Tax',
    excerpt: 'The open bets running behind every meeting, workout, dinner, and decision cost more than the number on the statement.',
    category: 'ATTENTION',
    readTime: '5 MIN READ',
    image: '/assets/page02-engine.jpg',
    imageAlt: 'Blue river currents cutting through a dark landscape',
    pullQuote: 'If part of your mind is always at the book, none of your life gets the full engine.',
    sections: [
      {
        heading: 'The bill arrives before the result.',
        paragraphs: [
          'Long before a wager settles, it begins charging rent. You check the line. Rebuild the scenario. Reach for the phone in the quiet second between tasks. The bet occupies attention whether it wins or loses.',
          'That fragmented state becomes normal quickly. You are present enough to function and absent enough to know you are leaving performance on the table.'
        ]
      },
      {
        heading: 'Attention is a control surface.',
        paragraphs: [
          'The first move is not forcing yourself to think about nothing. It is seeing where the mind is going before you automatically follow it.',
          'Observe the demand. Choose the line. Direct the energy into a target that pays you back. That sequence is trainable, and it is where command starts.'
        ]
      }
    ]
  },
  {
    slug: 'engine-vs-machine',
    number: '03',
    title: 'Engine vs. Machine',
    excerpt: 'Intensity is not the defect. The problem is what learned to steer it—and how quickly you can take the wheel back.',
    category: 'COMMAND',
    readTime: '7 MIN READ',
    image: '/assets/page03-other-stuff.jpg',
    imageAlt: 'A man standing waist-deep in a cold dark ocean',
    pullQuote: 'You do not need a smaller engine. You need command of the one you already have.',
    sections: [
      {
        heading: 'High output creates unusual fuel.',
        paragraphs: [
          'Competitive intensity, obsession, confidence, aggression, and an appetite for stakes are not foreign objects inside you. They are the same traits that let you build, lead, train, and endure when other people step away.',
          'Trying to erase those traits creates a second fight you do not need. The useful question is simpler: who is directing them right now?' 
        ]
      },
      {
        heading: 'Command is different from suppression.',
        paragraphs: [
          'Suppression says the urge should not exist. Command says the urge does not get the deciding vote.',
          'That difference matters. One turns every hard moment into proof that something is wrong with you. The other turns it into a repetition: notice, choose, direct.'
        ]
      }
    ]
  },
  {
    slug: 'easy-risk-real-risk',
    number: '04',
    title: 'Easy Risk vs. Real Risk',
    excerpt: 'The fastest available dose of uncertainty can quietly replace the difficult stakes that actually change a life.',
    category: 'PERFORMANCE',
    readTime: '6 MIN READ',
    image: '/assets/page07-day46.jpg',
    imageAlt: 'Dawn light breaking over dark ocean water',
    pullQuote: 'The drive does not need to die. It needs a target worthy of it.',
    sections: [
      {
        heading: 'Easy risk asks almost nothing up front.',
        paragraphs: [
          'No vulnerable conversation. No months of training. No chance that a room of people watches you fail. You can access uncertainty, competition, and consequence from the couch in seconds.',
          'That convenience is the trap. It offers a convincing substitute for courage while making the real arenas feel slower and less vivid.'
        ]
      },
      {
        heading: 'Move the stakes back into your life.',
        paragraphs: [
          'Real risk is specific. It has a name, a date, and another person who can see whether you showed up. It produces a result you can use even when the outcome is not the one you wanted.',
          'The work is not becoming a man who wants less. It is choosing stakes that deserve what you are capable of bringing.'
        ]
      }
    ]
  }
];

export const getInsight = (slug: string) => insights.find((insight) => insight.slug === slug);
